/* eslint-disable */
import React from "react";
import { Box, useMantineTheme, createStyles, Grid } from "@mantine/core";
import MessageInBox from "../messageInBox/messageInBox";
import { useGetConversations } from "api/chat/getConversation";
import { useGetMessages } from "api/chat/getspecificConversation";
import ListingMessage from "../messageListing/messagesListing";
import useChatSocket from "api/chat/chatSocket";
import { useUser } from "hooks/useUser";

type Conversation = {
  id: string;
  name: string;
  last: string;
  time: string;
  unread: number;
  status: "online" | "away" | "offline";
  type: "direct" | "group";
  otherUserId?: string;
};

type Message = {
  id: string;
  from: "them" | "me";
  text: string;
  time: string;
  senderName?: string;
};

const useStyles = createStyles((theme) => ({
  gradientPanel: {
    background: "linear-gradient(135deg,#6e4ef1 0%, #a46ef7 100%)",
  },
  container: {
    display: "grid",
    height: "100vh",
    width: "100%",
    gridTemplateColumns: "minmax(320px, 1fr) 1.6fr minmax(200px, 300px)",
    [theme.fn.smallerThan("md")]: {
      gridTemplateColumns: "1fr",
      gridAutoRows: "auto",
      height: "100vh",
    },
    boxSizing: "border-box",
  },
  leftPanel: {
    borderRight: `1px solid ${
      theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[2]
    }`,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    height: "88.9vh",
    backgroundColor:
      theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
  },
  searchBox: { marginBottom: 12 },
  convoItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 8px",
    borderRadius: 8,
    cursor: "pointer",
    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[7]
          : theme.colors.gray[0],
    },
  },
  centerPanel: {
    display: "flex",
    flexDirection: "column",
    height: "88.9vh",
    overflow: "hidden",
    backgroundColor:
      theme.colorScheme === "dark" ? theme.colors.dark[8] : theme.white,
    boxShadow:
      theme.colorScheme === "dark"
        ? "0 2px 10px rgba(0,0,0,0.6)"
        : "0 2px 8px rgba(12,12,12,0.04)",
  },
  chatHeader: {
    padding: "18px 24px",
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  messagesWrap: {
    flex: 1,
    padding: "2px 20px 2px 20px",
    position: "relative",
    overflowY: "auto",
  },
  messageRow: { maxWidth: "70%", marginBottom: 18 },
  incoming: {
    background: theme.colorScheme === "dark" ? theme.colors.dark[6] : "#f8f9fa",
    padding: "14px 18px",
    borderRadius: 12,
    boxShadow: "0 2px 6px rgba(12,12,12,0.06)",
  },
  outgoing: {
    marginLeft: "auto",
    background: "linear-gradient(90deg,#6e4ef1 0%, #9a6af6 100%)",
    color: "#fff",
    padding: "14px 18px",
    borderRadius: 12,
    boxShadow: "0 6px 18px rgba(110,78,241,0.14)",
  },
  typingIndicator: { padding: 12, color: theme.colors.gray[6] },
  rightPanel: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    backgroundColor:
      theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
  },
  bottomInput: {
    borderTop: `1px solid ${theme.colors.gray[2]}`,
    padding: 12,
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  activeConvo: {
    backgroundColor:
      theme.colorScheme === "dark"
        ? theme.colors.dark[6]
        : theme.colors.gray[1],
  },
}));

function formatTime(iso?: string) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso ?? "";
  }
}

function extractText(content: any): string {
  if (content == null) return "";
  if (typeof content === "string") return content;
  if (typeof content === "number" || typeof content === "boolean")
    return String(content);
  if (typeof content === "object") {
    if (typeof content.text === "string") return content.text;
    if (typeof content.body === "string") return content.body;
    if (typeof content.message === "string") return content.message;
    if (typeof content.content === "string") return content.content;
    try {
      return JSON.stringify(content);
    } catch {
      return "[unsupported message content]";
    }
  }
  return String(content);
}

function mapApiToMessages(
  apiResponse: any,
  activeConversation?: Conversation
): Message[] {
  if (!apiResponse || !Array.isArray(apiResponse.data)) return [];
  const otherId = activeConversation?.otherUserId;
  const currentUserId =
    typeof window !== "undefined" ? localStorage.getItem("userId") : null;

  return apiResponse.data.map((m: any) => {
    let from: "them" | "me" = "them";
    if (otherId) {
      from = m.sender === otherId ? "them" : "me";
    } else if (currentUserId) {
      from = m.sender === currentUserId ? "me" : "them";
    } else if (m.from) {
      from = m.from === "me" ? "me" : "them";
    }

    const rawContent = m.content ?? m.text ?? m.message ?? "";
    const text = extractText(rawContent);

    return {
      id: String(m.id ?? m._id ?? ""),
      from,
      text,
      time: formatTime(m.createdAt),
      senderName: undefined,
    } as Message;
  });
}

export default function ChatApp() {
  const { user } = useUser();

  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("project_hub_token") ?? ""
      : "";

  const {
    socket,
    emitTyping,
    emitRead,
    joinConversation,
    isUserOnline,
    onlineUsers,
    unreadMap,
    markConversationReadLocal,
    lastMessage,
  } = useChatSocket({ userId: user?._id, token });
  function mapApiToConversations(apiResponse: any): Conversation[] {
    if (!apiResponse || !Array.isArray(apiResponse.data)) return [];
    return apiResponse.data.map((item: any) => {
      const other = item.otherUser ?? {};
      const otherId = other.id ?? other._id ?? "";
      const status = isUserOnline
        ? isUserOnline(otherId)
          ? "online"
          : "offline"
        : "offline";
      console.log("other user", other, otherId, status);
      const name =
        `${other.firstName ?? ""} ${other.lastName ?? ""}`.trim() || "Unknown";
      const last = extractText(
        item.lastMessage?.content ?? item.lastMessage?.text ?? ""
      );
      const time = item.lastMessageAt
        ? formatTime(item.lastMessageAt)
        : formatTime(item.lastMessageAt ?? "");
      return {
        id: item.id ?? item._id ?? "",
        name,
        last,
        time,
        unread: item.unreadCount ?? 0,
        status: status,
        type: "direct",
        otherUserId: other.id ?? other._id ?? undefined,
      } as Conversation;
    });
  }
  const { classes, cx } = useStyles();
  const theme = useMantineTheme();
  const { data: convData } = useGetConversations();
  const apiConversations = React.useMemo(
    () => mapApiToConversations(convData),
    [convData]
  );
  const mergedConversations = React.useMemo(() => {
    return apiConversations.map((c) => ({
      ...c,
      unread:
        unreadMap && typeof unreadMap[c.id] === "number"
          ? unreadMap[c.id]
          : c.unread ?? 0,
      last:
        lastMessage && lastMessage.conversationId === c.id
          ? extractText(
              lastMessage.message.content ?? lastMessage.content ?? ""
            )
          : c.last,
    }));
  }, [apiConversations, unreadMap, lastMessage]);

  const conversationsSource =
    mergedConversations.length > 0 ? mergedConversations : [];
  const [segValue, setSegValue] = React.useState<"all" | "direct" | "group">(
    "all"
  );
  const [search, setSearch] = React.useState("");
  const [selectedConversationId, setSelectedConversationId] =
    React.useState<string>("");

  const {
    data: messagesData,
    isLoading: messagesLoading,
    error: messagesError,
  } = useGetMessages(selectedConversationId, 1, 1000);

  const visibleConversations = conversationsSource.filter((c) => {
    const matchesType = segValue === "all" ? true : c.type === segValue;
    const matchesSearch =
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.last ?? "").toLowerCase().includes(search.toLowerCase());
    return matchesType && matchesSearch;
  });

  const selectConversation = React.useCallback(
    (id: string) => {
      try {
        setSelectedConversationId(id);
        markConversationReadLocal?.(id);
      } catch (e) {
        console.warn("selectConversation failed", e);
      }
    },
    [markConversationReadLocal]
  );

  React.useEffect(() => {
    if (!selectedConversationId) return;
    try {
      joinConversation(selectedConversationId);
      emitRead({ conversationId: selectedConversationId });
      console.log("[ChatApp] joined and marked read:", selectedConversationId);
    } catch (e) {
      console.warn("joinConversation/emitRead failed", e);
    }
  }, [selectedConversationId, joinConversation, emitRead]);

  React.useEffect(() => {
    if (!visibleConversations.find((c) => c.id === selectedConversationId)) {
      if (visibleConversations.length > 0)
        setSelectedConversationId(visibleConversations[0].id);
      else setSelectedConversationId("");
    }
  }, [
    segValue,
    search,
    convData,
    // visibleConversations,
    selectedConversationId,
  ]);

  const activeConversation =
    conversationsSource.find((c) => c.id === selectedConversationId) ??
    conversationsSource[0];

  const messages = React.useMemo(() => {
    return mapApiToMessages(messagesData, activeConversation);
  }, [messagesData, activeConversation]);

  return (
    <Box
      p={0}
      styles={{
        main: {
          backgroundColor:
            theme.colorScheme === "dark"
              ? theme.colors.dark[8]
              : theme.colors.gray[0],
        },
      }}
    >
      <Grid gutter={0} sx={{ height: "10vh !important", margin: 0 }}>
        <Grid.Col span={4} style={{ padding: 0 }}>
          <div className={classes.leftPanel} style={{ padding: 0 }}>
            <ListingMessage
              visibleConversations={visibleConversations}
              search={search}
              setSearch={setSearch}
              segValue={segValue}
              setSegValue={setSegValue}
              selectedConversationId={selectedConversationId}
              setSelectedConversationId={selectConversation}
              classes={classes}
              cx={cx}
              emitRead={emitRead}
              isUserOnline={isUserOnline}
              onlineUsers={onlineUsers}
            />
          </div>
        </Grid.Col>

        <Grid.Col span={8} style={{ padding: 0 }}>
          <div className={classes.centerPanel} style={{ padding: 0 }}>
            <MessageInBox
              activeConversation={activeConversation}
              messages={messages}
              classes={classes}
              socket={socket}
              emitTyping={emitTyping}
              emitRead={emitRead}
              joinConversation={joinConversation}
              isUserOnline={isUserOnline}
              onlineUsers={onlineUsers}
            />
          </div>
        </Grid.Col>
      </Grid>
    </Box>
  );
}
